import { AxiosRequestConfig } from "axios";

import { moneefloApi } from "./client";

/**
 * Drop-in replacements for the Xano order-detail calls, so the screen that was
 * already built against them keeps working unchanged.
 *
 * Same reasoning as legacy-deal-api.ts: the migration changes where the
 * numbers come from, not what the screen looks like. This is a translation
 * layer with a finite life — when nothing imports it, the old shape is gone.
 *
 * One deliberate difference. Xano returned `pan_no` and `aadhaar_no` in full
 * to anyone who could open an order. Those are encrypted at rest now and only
 * the last four ever leave the database, so the fields carry "••••1234". That
 * is a downgrade in what the screen can display and an upgrade in what a
 * leaked response is worth.
 */

type Payload<T> = AxiosRequestConfig<T>;

const rupees = (minor: string | null | undefined): number =>
  Number(minor ?? 0) / 100;

type OrderDetail = {
  id: string;
  orderNo: string;
  createdBy: { name: string; email: string | null } | null;
  status: string;
  paymentStatus: string;
  placedAt: string | null;
  currency: string | null;
  fxAmountMinor: string | null;
  fxRate: string | null;
  totalPayableMinor: string | null;
  partnerCostMinor?: string | null;
  chargeTotalMinor: string;
  baseAmountMinor: string | null;
  settlementRate?: string | null;
  servicePartnerBankAccounts?: {
    id: string;
    nameOnAccount: string | null;
    bankName: string | null;
    accountNumber: string | null;
    ifsc: string | null;
    branchName: string | null;
    branchAddress: string | null;
    isDefault: boolean;
  }[];
  commissionByLevel?: Record<string, string>;
  /** The same cuts per unit of currency, which is what the screens use. */
  commissionRateByLevel?: Record<string, string>;
  /** What L1 keeps per unit: its commission plus the residual. */
  l1NetRate?: string | null;
  l1NetMinor?: string | null;
  purposeId?: string | null;
  purpose: string | null;
  remittance: { beneficiary: string | null } | null;
  charges: {
    type: string;
    label: string | null;
    amountMinor: string;
    taxMinor: string;
  }[];
  customer: {
    id: string;
    name: string;
    phone: string | null;
    email: string | null;
  };
  identities: { docType: string; last4: string | null; verified: boolean }[];
  payments: {
    id: string;
    reference: string | null;
    amountMinor: string;
    method: string;
    status: string;
    receivedAt: string | null;
  }[];
  originOrganization: OrgContact | null;
  servicePartnerOrganization: OrgContact | null;
};

type OrgContact = {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  city: string | null;
  state: string | null;
};

const masked = (
  identities: OrderDetail["identities"],
  docType: string,
): string => {
  const found = identities.find((i) => i.docType === docType);
  return found?.last4 ? `••••${found.last4}` : "";
};

const charge = (order: OrderDetail, type: string): number =>
  order.charges
    .filter((c) => c.type === type)
    .reduce((sum, c) => sum + rupees(c.amountMinor) + rupees(c.taxMinor), 0);

export type GetPartnerDetailsRequestType = Partial<{
  order_id: string | number;
  headers: { Authorization: string };
}>;

/**
 * The real Xano response nests the order under `result_1` and hangs the rest
 * off the top level; the exported type in service/apis/Orderdetails describes
 * an older, flatter shape the screen never actually used. This follows what
 * the screen reads.
 */
export async function getPartnerDetails(
  payload: Payload<GetPartnerDetailsRequestType>,
): Promise<{ data: Record<string, unknown> }> {
  const orderId = payload.data?.order_id;
  if (orderId === undefined) {
    throw new Error("order_id is not provided in the payload.");
  }

  const { data } = await moneefloApi.get<OrderDetail>(`/orders/${orderId}`);

  // The documents tab lists what has been filed against the order and what
  // has been asked for; without them the table is empty and Upload stays
  // disabled, because the button is gated on the list having entries.
  const documents = await moneefloApi
    .get(`/orders/${orderId}/documents`)
    .then((r) => r.data as {
      requirements: { id: string; label: string; satisfied: boolean }[];
      documents: {
        id: string;
        docType: string;
        fileName: string | null;
        label: string | null;
        status: string;
        uploadedAt: string;
        contentType: string | null;
        url?: string | null;
      }[];
    })
    // The tab is one part of a busy screen; a failure here should not
    // empty the rest of it.
    .catch(() => ({ requirements: [], documents: [] }));

  const DOC_STATUS: Record<string, string> = {
    verified: "Verified",
    pending: "Unverified",
    rejected: "Unverified",
    requested: "Requested",
  };

  const supporting = [
    // What has come in.
    ...documents.documents.map((d) => ({
      filename: d.label || d.fileName || d.docType,
      status: DOC_STATUS[d.status] ?? "Unverified",
      created_at: d.uploadedAt,
      document_id: d.id,
      doc_list_id: d.id,
      file: {
        url: d.url ?? "",
        type: d.contentType?.includes("pdf") ? "pdf" : "image",
      },
    })),
    // What is still outstanding, so the tab shows the whole checklist
    // rather than only the parts already satisfied.
    ...documents.requirements
      .filter((r) => !r.satisfied)
      .map((r) => ({
        filename: r.label,
        status: "Requested",
        created_at: null,
        document_id: null,
        doc_list_id: r.id,
        file: { url: "", type: "image" },
      })),
  ];

  const org = (o: OrgContact | null) => ({
    id: o?.id ?? "",
    name: o?.name ?? "",
    display_name: o?.name ?? "",
    phone: o?.phone ?? "",
    email: o?.email ?? "",
    address: [o?.city, o?.state].filter(Boolean).join(", "),
    legal_addr_city: o?.city ?? "",
    legal_addr_state: o?.state ?? "",
  });

  const paidMinor = data.payments.reduce(
    (sum, payment) => sum + Number(payment.amountMinor),
    0,
  );
  // The screens add these onto the settlement rate to work out what the
  // customer and each partner pay, so they need the rate per unit, not the
  // order's total commission. Handing over the amount showed a customer
  // rate of 434.50 on an order priced at 84.85.
  // .toString() is called on these unconditionally, so they are never absent.
  const commission = data.commissionRateByLevel ?? {};
  const commissionRate = (level: string): number =>
    Number(commission[level] ?? 0);

  // "My Margin" is what L1 actually keeps, which is its own commission
  // plus the residual — everything the customer paid above the settlement
  // rate once the provider and the partners beneath are paid. Reading the
  // commission list alone reported 0 on every chain with no rate-card row
  // for L1, which is most of them, even where the desk had set a margin
  // and the customer had been billed for it. `l1NetRate` is that figure;
  // the commission is the fallback for orders priced before it existed.
  const l1Rate =
    data.l1NetRate !== undefined && data.l1NetRate !== null
      ? Number(data.l1NetRate)
      : commissionRate("l1");

  return {
    data: {
      amount_paid: paidMinor / 100,
      student_name: null,
      // A deal and the order raised against it are one record here: the
      // deal is the order before it was placed, so the number is its own.
      // Xano kept two rows and a link between them.
      dealID: data.orderNo,
      channel: [data.originOrganization ? org(data.originOrganization) : {}],
      // Where the customer pays. The challan dialog reads Bank_name,
      // bank_acc_No and bank_ifsc_code off whichever is chosen.
      bank_list: (data.servicePartnerBankAccounts ?? []).map((bank) => ({
        id: bank.id,
        Bank_name: bank.bankName ?? "",
        bank_acc_No: bank.accountNumber ?? "",
        bank_ifsc_code: bank.ifsc ?? "",
        Name_on_acc: bank.nameOnAccount ?? "",
        Branch_Name: bank.branchName ?? "",
        branch_address: bank.branchAddress ?? "",
        Default: bank.isDefault,
      })),
      paymets_staff: [],
      result_1: {
        id: data.id,
        order_id: data.orderNo,
        status: data.status,
        payment_status: data.paymentStatus,
        created_at: data.placedAt,
        currency: data.currency ?? "",
        amount: rupees(data.fxAmountMinor),
        amount_inr: rupees(data.baseAmountMinor),
        payable_amount: rupees(data.totalPayableMinor),
        customer_rate: Number(data.fxRate ?? 0),
        gst: charge(data, "gst"),
        tcs: charge(data, "tcs"),
        tt_charges_inclgst: charge(data, "tt_charge"),
        foreign_bank_rates_inclgst: charge(data, "bank_charge"),
        service_partner_rates: data.settlementRate
          ? Number(data.settlementRate) / 1_000_000
          : 0,
        l1_commission_rates: l1Rate,
        l2_commission_rates: commissionRate("l2"),
        l3_commission_rates: commissionRate("l3"),
        l2_channel: null,
        l3_channel: null,
        _order_customers: {
          full_name: data.customer.name,
          email: data.customer.email ?? "",
          phone: data.customer.phone ?? "",
          // The card reads phone_number; without it every order showed
          // "Mobile: N/A" over a phone number the API had returned.
          phone_number: data.customer.phone ?? "",
          pan_no: masked(data.identities, "pan"),
          aadhaar_no: masked(data.identities, "aadhaar"),
          pan_soft_copy: { url: "" },
          aadhaar_soft_copy: { url: "" },
        },
        _order_service_partner: org(data.servicePartnerOrganization),
        _order_created_by: data.createdBy ? { fname: data.createdBy.name } : null,
        _beneficiary: data.remittance?.beneficiary
          ? { name: data.remittance.beneficiary }
          : null,
        // The id as well as the name: the refund wizard will not start
        // without it, and "Resume order" reads `.id` unguarded.
        _purpose: data.purpose
          ? { id: data.purposeId ?? "", name: data.purpose }
          : null,
        _service_partner_bank_details: (
          data.servicePartnerBankAccounts ?? []
        ).map((bank) => ({
          id: bank.id,
          Bank_name: bank.bankName ?? "",
          bank_acc_No: bank.accountNumber ?? "",
          bank_ifsc_code: bank.ifsc ?? "",
        })),
        _payments_of_order: data.payments.map((payment) => ({
          id: payment.id,
          acknowledgement_no: payment.reference ?? "",
          amount: String(rupees(payment.amountMinor)),
          receipt: { url: "" },
          supporting_doc: null,
          status_by_AD2: payment.status,
          notes: null,
        })),
        // Documents, challans, UTR capture and milestones are not migrated
        // yet. Empty is honest; the tabs render their own "nothing here" state.
        note: null,
        note_last_edited: null,
        reference_no: data.orderNo,
        doc_status: null,
        order_mileston: [],
        supporting_documents: supporting,
        swift_document: null,
        invoice_document: null,
        customer_discloser_id: [],
        opportunity_id: null,
        partner_detail: null,
        cancellation_charges: null,
        cancellation_reason: null,
        reason_for_refund: null,
        refund_order_id: null,
        refund_customer_rate: null,
        challan_generated: false,
        challan_url: null,
        challan_timestamp: null,
        challan_generated_by_staff: null,
        challan_generated_by_AD: null,
        utr_capture: [],
      },
    },
  };
}

export type PutOrderStatusRequestType = Partial<{
  order_id: string | number;
  status: string;
  headers: { Authorization: string };
}>;

/** Xano's order-status endpoint, against the state machine. */
export async function putOrderStatus(
  payload: Payload<PutOrderStatusRequestType>,
): Promise<{ data: unknown }> {
  const orderId = payload.data?.order_id;
  const status = payload.data?.status;
  if (orderId === undefined || !status) {
    throw new Error("order_id and status are required.");
  }
  const { data } = await moneefloApi.post(`/orders/${orderId}/status`, {
    status,
  });
  return { data };
}

/* ------------------------------------------------------- the orders table */

export type GetOrdersReqType = Partial<{
  headers: { Authorization: string };
  page: string | number;
  service_partner_id: number;
  per_page: number;
  start: string | null;
  end: string | null;
  status: string[];
  payment_status: string | string[];
  channel_of_interaction: (string | number)[];
  service_partner_id_filter: (string | number)[];
  partner_id: (string | number)[];
  q: string;
}>;

type OrderListRow = {
  id: string;
  orderNo: string;
  status: string;
  paymentStatus: string;
  product: string | null;
  customer: { id: string; name: string };
  currency: string | null;
  fxAmountMinor: string | null;
  totalPayableMinor: string | null;
  servicePartner: string | null;
  origin: { id: string; name: string; type: string } | null;
  placedAt: string | null;
};

/**
 * The chips speak Xano's status vocabulary and the API speaks its own.
 * "orderCreated" and "initiated" are not statuses here, so forwarding them
 * verbatim made the request 400; the table swallows the error and keeps
 * showing the previous, unfiltered page, so the rows no longer match the
 * chips the operator pressed.
 */
const CHIP_STATUS: Record<string, string> = {
  orderCreated: 'draft,quoted,confirmed',
  initiated: 'draft,quoted,confirmed',
  ordercreated: 'draft,quoted,confirmed',
};

const statusList = (value: unknown): string | undefined => {
  const chips = (Array.isArray(value) ? value : [value])
    .filter((item) => item !== null && item !== undefined && item !== '')
    .map(String);
  if (chips.length === 0) return undefined;
  const statuses = new Set<string>();
  for (const chip of chips) {
    for (const status of (CHIP_STATUS[chip] ?? chip).split(',')) {
      statuses.add(status);
    }
  }
  return [...statuses].join(',');
};

const list = (value: unknown): string | undefined => {
  const items = (Array.isArray(value) ? value : [value])
    .filter((item) => item !== null && item !== undefined && item !== "")
    .map(String);
  return items.length > 0 ? items.join(",") : undefined;
};

/**
 * The orders table, with its filter chips.
 *
 * Xano wrapped the page in `result1` and the screen reads it there, so the
 * envelope is rebuilt rather than unwrapped. The chips narrow on the API
 * rather than in the browser, because this table pages server-side.
 *
 * How an order arrived is read off the organization that took it: a branch
 * means someone walked in, a partner means they were sent. Xano recorded an
 * employee referral as a third kind and there is nothing here that
 * distinguishes it, so those read "N/A" rather than being guessed at.
 */
export async function GetFilterOrders(
  payload: Payload<GetOrdersReqType> & { params?: GetOrdersReqType },
): Promise<{ data: { result1: Record<string, unknown> } }> {
  const query = payload.params ?? payload.data ?? {};
  const page = Number(query.page) > 0 ? Number(query.page) : 1;
  const perPage = Math.min(Number(query.per_page) || 25, 100);

  const { data } = await moneefloApi.get("/orders", {
    params: {
      // Xano's order table held remittance and nothing else -- forex, visa
      // and travel lived in generic_order_details -- so this endpoint was
      // remittance-only without having to say so. One table holds every
      // product now, so it has to say so.
      category: "remittance",
      page,
      perPage,
      search: query.q?.trim() || undefined,
      status: statusList(query.status),
      paymentStatus: list(query.payment_status),
      xanoServicePartnerIds: list(query.service_partner_id_filter),
      originOrgIds: list(query.partner_id),
      placedFrom: query.start ?? undefined,
      placedTo: query.end ?? undefined,
    },
  });

  const items = (data?.items ?? []) as OrderListRow[];
  const total = Number(data?.total ?? items.length);
  const pageTotal = Math.max(1, Math.ceil(total / perPage));

  return {
    data: {
      result1: {
        items: items.map((order) => ({
          // The screen renders "ORD-" in front of whatever it is given, so
          // the house prefix comes off here: MF-R005044 shows as ORD-R005044
          // rather than ORD-MF-R005044.
          id: order.orderNo.replace(/^(ORD-)?MF-/, ""),
          _order_customers: { full_name: order.customer.name },
          customers_id: order.customer.id,
          status: order.status,
          payment_status: order.paymentStatus,
          payable_amount: Number(order.totalPayableMinor ?? 0) / 100,
          amount_inr: Number(order.totalPayableMinor ?? 0) / 100,
          created_at: order.placedAt ? new Date(order.placedAt).getTime() : null,
          _order_service_partner: { display_name: order.servicePartner ?? "" },
          _branch: { name: order.origin?.name ?? "" },
          createdBy: { name: order.origin?.name ?? "" },
          _opportunity: [
            {
              channel_of_interaction_id:
                order.origin?.type === "branch"
                  ? 7
                  : order.origin?.type === "partner"
                    ? 12
                    : null,
            },
          ],
        })),
        pageTotal,
        curPage: page,
        itemsReceived: items.length,
        itemsTotal: total,
        offset: (page - 1) * perPage,
        perPage,
        nextPage: page < pageTotal ? page + 1 : null,
        prevPage: page > 1 ? page - 1 : null,
      },
    },
  };
}

/**
 * The status, in the words the forex table keys its colours by.
 *
 * Xano's generic orders carried a longer vocabulary ("Docs Pending",
 * "Delivery Pending"); the import collapsed it onto the one lifecycle, so the
 * exact wording is gone. The nearest word in the screen's own list is used
 * rather than leaving the cell blank, which is what an unrecognised status
 * renders as.
 */
const FOREX_STATUS: Record<string, string> = {
  draft: "Order Created",
  quoted: "Order Created",
  confirmed: "Order Accepted",
  processing: "processing",
  on_hold: "Docs Pending",
  fulfilled: "fulfilled",
  cancelled: "Order Cancelled",
  refunded: "Order Closed",
};

/**
 * The forex orders table.
 *
 * Xano had a separate endpoint returning a flatter row than the remittance
 * one: no `result1` wrapper, and the money arrives as a list of product lines
 * the screen sums. The new orders list is one endpoint for every product, so
 * this narrows it to the forex category and reshapes.
 */
export async function GetForexOrders(
  payload: Payload<GetOrdersReqType> & { params?: GetOrdersReqType },
): Promise<{ data: Record<string, unknown> }> {
  const query = payload.params ?? payload.data ?? {};
  const page = Number(query.page) > 0 ? Number(query.page) : 1;
  const perPage = Math.min(Number(query.per_page) || 25, 100);

  const { data } = await moneefloApi.get("/orders", {
    params: {
      category: "forex",
      page,
      perPage,
      search: query.q?.trim() || undefined,
      status: statusList(query.status),
      paymentStatus: list(query.payment_status),
      xanoServicePartnerIds: list(query.service_partner_id_filter),
      originOrgIds: list(query.partner_id),
    },
  });

  const items = (data?.items ?? []) as OrderListRow[];
  const total = Number(data?.total ?? items.length);
  const pageTotal = Math.max(1, Math.ceil(total / perPage));

  return {
    data: {
      items: items.map((order) => {
        const inr = Number(order.totalPayableMinor ?? 0) / 100;
        return {
          id: order.orderNo.replace(/^(ORD-)?MF-/, ""),
          contactName: order.customer.name,
          customer_name: order.customer.name,
          created_at: order.placedAt,
          status: FOREX_STATUS[order.status] ?? order.status,
          maxx_status: FOREX_STATUS[order.status] ?? order.status,
          channel: order.origin?.name ?? "N/A",
          grand_total: inr.toFixed(2),
          // The screen sums these itself, so the order's own totals go across
          // as a single line rather than being split into ones that do not
          // exist.
          product: [
            {
              fx_amount: Number(order.fxAmountMinor ?? 0) / 100,
              inr_amount: inr,
              currency: order.currency,
            },
          ],
        };
      }),
      pageTotal,
      curPage: page,
      itemsReceived: items.length,
      itemsTotal: total,
      offset: (page - 1) * perPage,
      perPage,
      nextPage: page < pageTotal ? page + 1 : null,
      prevPage: page > 1 ? page - 1 : null,
    },
  };
}

/* ------------------------------------------------- the remittance overview */

export type GetOrderStatsRequestType = Partial<{
  headers: { Authorization: string };
}>;

export type GetOrderStatsV2ResponseType = {
  ad2_cur_month_amnt: number;
  ad1_cur_month_amnt: number;
  ad1_prev_month_amnt: number;
  ad2_prev_month_amnt: number;
};

/**
 * This month's order value against last month's, for the overview cards.
 *
 * Xano split the figures by authorised-dealer licence. Nothing records which
 * licence an order went out under, and the AD1 card was commented out of the
 * screen long before this migration, so the whole figure sits under AD2 and
 * AD1 reads zero. The total the screen adds up is right either way; only the
 * split is unavailable.
 */
export async function getOrderStatsV2(
  _payload: Payload<GetOrderStatsRequestType>,
): Promise<{ data: GetOrderStatsV2ResponseType }> {
  const { data } = await moneefloApi.get("/dashboard/stats", {
    params: { category: "remittance" },
  });
  const orders = (data?.orders ?? {}) as {
    thisMonthValueMinor?: string;
    lastMonthValueMinor?: string;
  };
  return {
    data: {
      ad2_cur_month_amnt: rupees(orders.thisMonthValueMinor),
      ad2_prev_month_amnt: rupees(orders.lastMonthValueMinor),
      ad1_cur_month_amnt: 0,
      ad1_prev_month_amnt: 0,
    },
  };
}

/* ---------------------------------------------- an order's documents ---- */

export type GetReqDocListReqType = Partial<{
  headers: { Authorization: string };
  order_id: string;
}>;

/** A uuid is one of our documents; a number is a requirement. */
const isDocumentId = (value: unknown): boolean =>
  typeof value === "string" &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);

/**
 * The documents that can be asked for on an order: whatever the purpose and
 * the service partner require. The dialog also lets the operator type a name
 * of their own, so this is a starting list rather than the only choices.
 */
export async function getReqDocList(
  payload: Payload<GetReqDocListReqType> & {
    params?: { order_id?: string };
  },
): Promise<{ data: { id: string; filename: string }[] }> {
  const orderId = payload.params?.order_id ?? payload.data?.order_id;
  if (!orderId) return { data: [] };

  const { data } = await moneefloApi.get(
    `/orders/${encodeURIComponent(orderId)}/documents`,
  );
  const { requirements } = data as {
    requirements: { id: string; label: string }[];
  };
  return {
    data: requirements.map((r) => ({ id: r.id, filename: r.label })),
  };
}

/** The forex variant, which has no separate rule set yet. */
export const getReqForexOrderDocList = getReqDocList;

export type POSTOrderDocumentsRequestType = Partial<{
  headers: { Authorization: string };
  order_id: string;
  doc_list_id: string | null;
  file: string | null;
  status: string;
  file_name: string | null;
  filename: string | null;
}>;

/**
 * Three different things behind one Xano endpoint, told apart by what is
 * in the payload: asking the customer for a document, uploading one, and
 * marking one verified.
 */
export async function postOrderDocuments(
  payload: Payload<POSTOrderDocumentsRequestType>,
): Promise<{ data: unknown; status: number }> {
  const body = payload.data ?? {};
  const orderId = body.order_id;
  if (!orderId) throw new Error("An order is required.");

  const ref = body.doc_list_id ?? undefined;
  const name = body.filename ?? body.file_name ?? undefined;

  // Asking for one that is not in yet.
  if (body.status === "Requested") {
    const { data } = await moneefloApi.post(
      `/orders/${encodeURIComponent(orderId)}/documents/request`,
      {
        docType: (name || "other").toLowerCase().slice(0, 40),
        requirementId: ref && !isDocumentId(ref) ? ref : undefined,
        label: name,
      },
    );
    return { data, status: 200 };
  }

  // Uploading or replacing a file.
  if (body.file) {
    const form = new FormData();
    const safe = (name || "document").replace(/[^\w.\- ]+/g, "_");
    form.append("file", fileFromDataUrl(body.file, `${safe}.pdf`));
    form.append("docType", (name || "other").toLowerCase().slice(0, 40));
    if (ref && !isDocumentId(ref)) form.append("requirementId", ref);
    const { data } = await moneefloApi.post(
      `/orders/${encodeURIComponent(orderId)}/documents`,
      form,
    );
    return { data, status: 200 };
  }

  // No file and not a request: the operator is approving what is there.
  if (ref && isDocumentId(ref)) {
    const { data } = await moneefloApi.post(`/documents/${ref}/verify`, {
      status: "verified",
    });
    return { data, status: 200 };
  }

  throw new Error("Nothing to do: no file, and no document to verify.");
}

/** A data URL, as the screens stage a picked file, back into bytes. */
export function fileFromDataUrl(dataUrl: string, name: string): File {
  const [head, encoded] = dataUrl.split(",");
  const type = /data:([^;]+)/.exec(head)?.[1] ?? "application/octet-stream";
  // A name carried in the URL (see fileToDataUrl) is the person's own file name, and wins over a made-up one.
  const carried = /;name=([^;,]+)/.exec(head)?.[1];
  if (carried) {
    try {
      name = decodeURIComponent(carried);
    } catch {
      /* keep the given name */
    }
  }
  const binary = atob(encoded ?? "");
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new File([bytes], name, { type });
}

/**
 * A file as a data URL that also carries its own name, so the screens that pass
 * documents around as text do not lose the file's real name and type on the way
 * to core. `fileFromDataUrl` reads the name back.
 */
export function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error("The file could not be read."));
    reader.onload = () => {
      const url = String(reader.result ?? "");
      const at = url.indexOf(";base64,");
      resolve(at === -1 ? url : `${url.slice(0, at)};name=${encodeURIComponent(file.name)}${url.slice(at)}`);
    };
    reader.readAsDataURL(file);
  });
}

/* --------------------------------------------- forex rates over the counter */

/**
 * The rate to quote for physical currency or a travel card.
 *
 * Xano's `fx_live_rates`. Same answer, worked out here: the market rate
 * from the live feed, either side by the spread held for that currency,
 * direction and product.
 */
export async function getLiveRates(args: {
  currency: string;
  /** "Buy" when the customer is buying from us, as the screens send it. */
  type: string;
  fx_product: string;
}): Promise<{ rates: number }> {
  const { data } = await moneefloApi.post("/pricing/forex-rate", {
    currency: args.currency,
    type: args.type,
    fxProduct: args.fx_product,
  });
  return data as { rates: number };
}

export type ForexCalculationPayload = { amount: number; type?: string };

/**
 * GST on a counter forex conversion, band by band.
 *
 * Xano's `forex_price_breakdown_portal_v3`. Rule 32(2)(b), the same
 * calculation a remittance uses, split into its bands because the forex
 * screens show them separately.
 */
export async function getCalculations(
  args: ForexCalculationPayload,
): Promise<{
  amount: number;
  CGST: number;
  SGST: number;
  /** An inter-state supply would be this instead of CGST and SGST.
   *  Nothing decides place of supply yet, so it is never set. */
  IGST?: number;
  roundOff: number;
  service_charge: number;
  gst: {
    slab1: number;
    slab2: number;
    slab3: number;
    total_taxable_amount: number;
    gst: number;
  };
  total_payable: { grand_total: number };
}> {
  const { data } = await moneefloApi.get("/pricing/forex-gst", {
    params: { amount: args.amount ?? 0, type: args.type },
  });
  return data as Awaited<ReturnType<typeof getCalculations>>;
}
